import './ShipmentsList.scss';

import { getDefaultSortOrder, List, ShowButton, useTable } from '@refinedev/antd';
import { BaseRecord } from '@refinedev/core';
import { Flex, Space, Tooltip } from 'antd';
import dayjs from 'dayjs';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import AppTable from '@/components/general/AppTable';
import LinkTextField from '@/components/general/LinkTextField';
import PackagesListFilter from '@/components/logistic/PackagesListFilter/PackagesListFilter';
import PackageStateTag from '@/components/logistic/PackageStateTag';
import ProtectedCreateButton from '@/components/protected/ProtectedCreateButton';
import { COMPANY_URLS } from '@/constants/urls';
import { usePermissionContext } from '@/contexts/PermissionContext';
import { useUserContext } from '@/contexts/UserContext';
import { useListStatePersistence } from '@/hooks/useListStatePersistence';
import { PACKAGE_STATES } from '@/model/Logistic/PackageStates';
import { Shipment } from '@/model/Logistic/Shipment/Shipment';
import { SHIPMENT_TYPE } from '@/model/Logistic/Shipment/ShipmentType';
import { RESOURCES } from '@/Resources';

export const ShipmentsList = () => {
  const { t } = useTranslation();
  const { hasPermission } = usePermissionContext();
  const { pageSizePreference } = useUserContext();
  const [isReady, setIsReady] = useState(false);
  const {
    tableProps,
    filters,
    setFilters,
    currentPage,
    setCurrentPage: setCurrent,
    pageSize,
    setPageSize,
    sorters,
    setSorters,
  } = useTable<Shipment>({
    sorters: {
      initial: [{ field: 'id', order: 'desc' }],
      mode: 'server',
    },
    syncWithLocation: true,
    queryOptions: {
      refetchInterval: 300000, // 5 minutes
      retry: 3,
      enabled: isReady,
    },
    pagination: {
      pageSize: pageSizePreference,
    },
  });

  // Persistencia de filtros y paginado del listado.
  useListStatePersistence({
    resource: RESOURCES.SHIPMENTS,
    filters,
    setFilters,
    sorters,
    setSorters,
    currentPage,
    setCurrentPage: setCurrent,
    pageSize,
    setPageSize,
    onReady: () => setIsReady(true),
  });

  return (
    <List
      headerButtons={({ createButtonProps }) => (
        <>
          <PackagesListFilter filters={filters} setFilters={setFilters} setCurrent={setCurrent} />
          <ProtectedCreateButton {...createButtonProps} />
        </>
      )}>
      <AppTable
        {...tableProps}
        pagination={{
          ...tableProps.pagination,
          pageSizeOptions: [20, 40, 60, 80], // Opciones de cantidad por página
          showSizeChanger: true, // Muestra el selector
          showTotal: (total, range) => {
            return `${range[0]} - ${range[1]} ${t('de')} ${total}`;
          },
        }}
        rowClassName={record => {
          return (
            'ShipmentTableRow ' + (record.status != undefined ? PACKAGE_STATES[record.status] : '')
          );
        }}
        rowKey="id"
        scroll={{ x: 1200, y: 'calc(100dvh - 310px)' }}>
        <AppTable.Column
          dataIndex="id"
          title={'Id'}
          width={65}
          sorter={{ multiple: 1 }}
          sortOrder={getDefaultSortOrder('id', sorters)}
          render={value => (
            <LinkTextField label={value} to={`/shipments/show/${value}`} local withoutColor />
          )}
        />
        <AppTable.Column
          title={t('Destino')}
          width={240}
          render={(_, record) => (
            <LinkTextField
              label={
                record.company?.full_name ? `${record.company.full_name}` : `${record.company.id}`
              }
              to={`${COMPANY_URLS.ADMIN_EMPRESA}/${record.company.id}`}
              admin
              withoutColor
            />
          )}
        />

        <AppTable.Column
          title={t('Operador logístico')}
          width={160}
          render={(_, record) =>
            record.shipper ? (
              <LinkTextField
                label={`${record.shipper.name}`}
                to={'/shippers/show/' + record.shipper.id}
                local
                withoutColor
              />
            ) : (
              '-'
            )
          }
        />

        <AppTable.Column
          width={100}
          title={'Ticket'}
          render={(_, record) =>
            record.ticket ? (
              <LinkTextField
                label={`${record.ticket.id}`}
                to={`/tickets/show/${record.ticket.id}`}
                local
                withoutColor
              />
            ) : (
              '-'
            )
          }
        />
        <AppTable.Column
          title={t('Empresa del ticket')}
          width={240}
          render={(_, record) =>
            record?.ticket?.empresa ? (
              <LinkTextField
                label={
                  record.ticket?.empresa?.full_name
                    ? `${record.ticket.empresa.full_name}`
                    : `${record.ticket.empresa.id}`
                }
                to={`${COMPANY_URLS.ADMIN_EMPRESA}/${record.company.id}`}
                admin
                withoutColor
              />
            ) : (
              '-'
            )
          }
        />
        <AppTable.Column
          width={150}
          dataIndex="date_estimated"
          title={t('Fecha estimada')}
          sorter={{ multiple: 2 }}
          sortOrder={getDefaultSortOrder('date_estimated', sorters)}
          render={(_, record) =>
            record.date_estimated ? dayjs(record.date_estimated).format('L') : '-'
          }
        />
        <AppTable.Column
          width={150}
          dataIndex="date"
          title={t('Fecha efectiva')}
          sorter={{ multiple: 2 }}
          sortOrder={getDefaultSortOrder('date', sorters)}
          render={(_, record) => (record.date ? dayjs(record.date).format('L') : '-')}
        />
        <AppTable.Column
          width={70}
          dataIndex="tipo"
          title={t('Tipo')}
          render={(_, record) => (
            <Flex align="center" justify="center">
              {record?.tipo == SHIPMENT_TYPE.AUTOMATICO ? (
                <Tooltip title={t('Envío creado automáticamente')}>🤖</Tooltip>
              ) : (
                <Tooltip title={t('Envío creado manualmente')}>✋🏼</Tooltip>
              )}
            </Flex>
          )}
        />
        <AppTable.Column
          width={150}
          dataIndex="created_by"
          title={t('Creado por')}
          render={(_, record) => `${record.created_by.first_name} ${record.created_by.last_name}`}
        />
        <AppTable.Column
          fixed="right"
          width={150}
          dataIndex={'status'}
          render={v => (
            <Flex vertical align="center" justify="center" style={{ background: 'none' }}>
              <PackageStateTag packageStatus={v} />
            </Flex>
          )}
        />
        {hasPermission('show') && (
          <AppTable.Column
            dataIndex="actions"
            width={55}
            fixed="right"
            render={(_, record: BaseRecord) => (
              <Space>
                <ShowButton hideText size="small" recordItemId={record.id} />
              </Space>
            )}
          />
        )}
      </AppTable>
    </List>
  );
};
